/**
 * HUD (W2-01a preview-gl; rendering.md R§7, R§8 step 9, ARCHITECTURE.md §7.3 step 7).
 *
 * The engine lays the HUD out in UI space (y down) for both attach-UI layouts and gives every element its attach
 * transform `m` (pivots included), colour and whole-HUD alpha (res.alpha). This pass draws:
 *  - rects (pause bars, progress bar and tick) in UI space under `m`, each with its own colour;
 *  - texts at glyph PxScale 0.04 * size * W px = 0.08 * size UI units, in the `pgr` face (score, combo, labels;
 *    Saira at phigros.ttf's metrics) or the `ui` face (name, level). Phira's single-line box is `ascent` high and as
 *    wide as the advance; anchor (ax, ay) moves it by (-ax w, -ay h), so ay = 0 puts the ascent line on pos.y and
 *    ay = 1 the baseline. `maxWidth` truncates with an ellipsis.
 */

import { HUD_EM } from '../../core/engine/notes/hud';
import type { HudElementDraw } from '../../core/engine/types';
import { colorByte } from '../gl/quadBatch';
import { truncateToWidth, type FontRole } from './fonts';
import { rasterRect, textPxScale } from './linePass';
import { beginQuads, type PassContext } from './passContext';

/** The text a HUD element shows (single line, truncated to maxWidth UI units). */
export function hudText(ctx: Pick<PassContext, 'fonts'>, el: HudElementDraw): string {
  const raw = (el.text ?? '').replace(/[\r\n]+/g, ' ');
  if (raw === '' || el.maxWidth === undefined) return raw;
  const role: FontRole = el.font ?? 'ui';
  const em = HUD_EM * (el.size ?? 1);
  return truncateToWidth(raw, el.maxWidth / em, (s) => ctx.fonts.advance(role, s));
}

function drawRects(ctx: PassContext, el: HudElementDraw, alpha: number): void {
  const rects = el.rects;
  if (rects === undefined || rects.length === 0) return;
  beginQuads(ctx, ctx.uiToClip, null);
  for (const [x, y, w, h, c] of rects) {
    const a = colorByte(c[3] * alpha);
    if (a === 0 || !(w > 0) || !(h > 0)) continue;
    ctx.batch.rect(el.m, x, y, x + w, y + h, 0, 0, 1, 1, colorByte(c[0]), colorByte(c[1]), colorByte(c[2]), a);
  }
}

function drawElementText(ctx: PassContext, el: HudElementDraw, alpha: number): void {
  const text = hudText(ctx, el);
  if (text === '') return;
  const a = colorByte(el.color[3] * alpha);
  if (a === 0) return;
  const role: FontRole = el.font ?? 'ui';
  const em = HUD_EM * (el.size ?? 1);
  if (!(em > 0)) return;
  const px = textPxScale(el.m, em, ctx.pxPerUnitX, ctx.pxPerUnitY);
  if (!(px >= 0.5)) return;
  const r = ctx.text.get(text, role, px);
  if (r === null) return;
  const tex = ctx.textures.get(r.canvas);
  if (tex === null) return;
  const w = r.blockW * em;
  const h = r.ascent * em;
  const left = el.pos[0] - el.anchor[0] * w;
  const top = el.pos[1] - el.anchor[1] * h;
  const [x0, y0, x1, y1] = rasterRect(r, left, top, em);
  beginQuads(ctx, ctx.uiToClip, tex);
  ctx.batch.rect(
    el.m,
    x0,
    y0,
    x1,
    y1,
    0,
    0,
    1,
    1,
    colorByte(el.color[0]),
    colorByte(el.color[1]),
    colorByte(el.color[2]),
    a,
  );
}

export function drawHud(ctx: PassContext): void {
  const hud = ctx.list.hud;
  if (hud === null) return;
  const alpha = hud.alpha;
  if (!(alpha > 0)) return;
  for (const el of hud.elements) {
    if (!el.visible) continue;
    drawRects(ctx, el, alpha);
    drawElementText(ctx, el, alpha);
  }
}
